import React, { useState, useEffect } from "react";
import PageTableHeader from "../../../components/pageTableHeader/pageTableHeader";
import CommonTable from "../../../components/table/table";
import { useCommonFetch } from "../../../../hooks/useCommonFetch";
import { useSelector } from "react-redux";
import {
  getFinanceQueue,
  processInstantPayout,
  addToPayroll,
  bulkAddToPayroll,
  markAsPaid,
  // getPayoutProviderStatus, // COMMENTED OUT - Not needed currently
  checkKYCStatus,
} from "../../../../services/workspace/reimbursement.service";
import { useNavigate } from "react-router-dom";
import { toast } from "react-hot-toast";
import DefaultButton from "../../../components/buttons/defaultButton";
import CommonModal from "../../../components/modals/commonModel";
import DefaultBadge from "../../../components/badge/defaultBadge";
import DefaultCheckbox from "../../../components/inputs/defaultCheckbox";
import DefaultTextarea from "../../../components/inputs/defaultTextarea";
import { FiDollarSign, FiBriefcase, FiCheckCircle, FiAlertCircle } from "react-icons/fi";

const items = [
  { label: "Reimbursement", path: "/reimbursement" },
  { label: "Finance Queue", path: "" },
];

const FinanceQueue = () => {
  const { limit } = useSelector((state) => state.global);
  const navigate = useNavigate();

  const [modal, setModal] = useState({
    show: false,
    title: "",
    message: "",
    actionType: null,
    item: null,
    loading: false,
    notes: "",
  });

  const [selectedItems, setSelectedItems] = useState([]);
  // COMMENTED OUT - Payout provider status not needed currently
  // const [payoutProviderStatus, setPayoutProviderStatus] = useState(null);

  // useEffect(() => {
  //   checkPayoutProvider();
  // }, []);

  // const checkPayoutProvider = async () => {
  //   try {
  //     const response = await getPayoutProviderStatus();
  //     setPayoutProviderStatus(response.data);
  //   } catch (error) {
  //     console.error("Failed to check payout provider:", error);
  //   }
  // };

  const {
    loading,
    data: tableData,
    total: totalData,
    currentPage,
    handleSearchChange,
    handlePageChange,
    fetchData,
  } = useCommonFetch(getFinanceQueue, {
    dataPath: "data.data",
    totalPath: "data.meta.total",
    errorMessage: "Failed to fetch finance queue!",
    debounceDelay: 500,

    dataTransformer: (data) => {
      if (!Array.isArray(data)) return { data: [], action: {} };

      const transformedData = data.map((item) => {
        // Preserve original employee data before transformation
        const originalEmployee = item.employee;
        return {
          ...item,
          id: { data: item.uuid, variant: "text" },
          checkbox: {
            variant: "custom",
            render: () => (
              <DefaultCheckbox
                checked={selectedItems.includes(item.uuid)}
                onChange={(e) => {
                  if (e.target.checked) {
                    setSelectedItems([...selectedItems, item.uuid]);
                  } else {
                    setSelectedItems(selectedItems.filter((id) => id !== item.uuid));
                  }
                }}
              />
            ),
          },
          expense_number: {
            data: item.expense_number || "-",
            variant: "text",
            className: "font-semibold text-primary-600",
          },
          employee: {
            data: originalEmployee?.name || "-",
            variant: "text",
            className: "font-medium",
          },
          bank_details: {
            data: originalEmployee?.bank_account_number || "-",
            variant: "text",
          },
          kyc_status: {
            variant: "custom",
            data: originalEmployee?.kyc_verified ? "Verified" : "Pending",
          },
          category: {
            data: item.category?.name || "-",
            variant: "text",
          },
          amount: {
            data: item.amount || 0,
            variant: "currency",
            className: "font-semibold text-gray-900",
          },
          approved_at: {
            data: item.approved_at || "-",
            variant: item.approved_at ? "date-time" : "text",
          },
          payment_status: {
            variant: "custom",
            render: () => {
              const statusMap = {
                APPROVED: { label: "Pending Payment", color: "yellow" },
                PROCESSING: { label: "Processing", color: "blue" },
                PAID: { label: "Paid", color: "green" },
                ADDED_TO_PAYROLL: { label: "Added to Payroll", color: "purple" },
              };
              const config = statusMap[item.status] || statusMap.APPROVED;
              return <DefaultBadge name={config.label} color={config.color} />;
            },
          },
          // Preserve original employee data for action buttons
          _originalEmployee: originalEmployee,
        };
      });

      const actionButton = {
        view: (item) => navigate(`/reimbursement/expenses/${item.id.data}`, { state: { from: 'finance-queue' } }),

        instantPayout: (item) =>
          setModal({
            show: true,
            actionType: "instant_payout",
            item,
            title: "Process Instant Payout",
            message: `Process instant payout of ₹${item.amount.data} for ${item.employee.data}?`,
            notes: "",
          }),

        addToPayroll: (item) =>
          setModal({
            show: true,
            actionType: "add_to_payroll",
            item,
            title: "Add to Payroll",
            message: `Add ₹${item.amount.data} to next payroll for ${item.employee.data}?`,
            notes: "",
          }),

        markPaid: (item) =>
          setModal({
            show: true,
            actionType: "mark_paid",
            item,
            title: "Mark as Paid",
            message: `Mark expense ${item.expense_number.data} as paid (manual confirmation)?`,
            notes: "",
          }),

        showInstantPayout: (item) => {
          // Show only if payout provider is enabled, KYC verified, and bank details available
          // COMMENTED OUT - Payout provider check disabled
          // Use original employee data from _originalEmployee
          const employee = item._originalEmployee || item.employee;
          const kycVerified = employee?.kyc_verified;
          const isVerified = 
            kycVerified === true || 
            kycVerified === 1 || 
            kycVerified === "1" || 
            (kycVerified && String(kycVerified).toLowerCase() === "true");
          
          return (
            item.status.data === "APPROVED"
            // && payoutProviderStatus?.is_enabled
            && isVerified &&
            employee?.bank_account_number
          );
        },
        showAddToPayroll: (item) => item.status.data === "APPROVED",
        showMarkPaid: (item) => item.status.data === "PROCESSING",
      };

      return { data: transformedData, action: actionButton };
    },
  });

  const handleActionConfirm = async () => {
    if (!modal.item) return;

    setModal((prev) => ({ ...prev, loading: true }));

    try {
      const payload = { notes: modal.notes };

      if (modal.actionType === "instant_payout") {
        // Check KYC before processing
        const kycResponse = await checkKYCStatus(modal.item.employee.id);
        if (!kycResponse.data.kyc_verified) {
          toast.error("Employee KYC not verified. Cannot process instant payout.");
          setModal((prev) => ({ ...prev, loading: false }));
          return;
        }

        await processInstantPayout(modal.item.id.data, payload);
        toast.success("Instant payout initiated successfully!");
      } else if (modal.actionType === "add_to_payroll") {
        await addToPayroll(modal.item.id.data, payload);
        toast.success("Expense added to next payroll!");
      } else if (modal.actionType === "mark_paid") {
        if (!modal.notes.trim()) {
          toast.error("Please provide payment confirmation details");
          setModal((prev) => ({ ...prev, loading: false }));
          return;
        }
        await markAsPaid(modal.item.id.data, payload);
        toast.success("Expense marked as paid!");
      }

      fetchData();
      setModal({ show: false, item: null, actionType: null, notes: "" });
    } catch (err) {
      toast.error(
        err?.response?.data?.message || `Failed to process expense`
      );
      setModal((prev) => ({ ...prev, loading: false }));
    }
  };

  const handleBulkAddToPayroll = async () => {
    if (selectedItems.length === 0) {
      toast.error("Please select expenses to add to payroll");
      return;
    }

    try {
      await bulkAddToPayroll({ expense_ids: selectedItems });
      toast.success(
        `${selectedItems.length} expense(s) added to next payroll successfully!`
      );
      setSelectedItems([]);
      fetchData();
    } catch (error) {
      toast.error("Failed to bulk add expenses to payroll");
    }
  };

  const TableHeaderData = [
    { label: "Expense ID", key: "expense_number" },
    { label: "Employee", key: "employee" },
    { label: "Category", key: "category" },
    { label: "Amount", key: "amount" },
    { label: "Bank Details", key: "bank_details", sortable: false },
    { label: "KYC Status", key: "kyc_status", sortable: false },
    { label: "Approved At", key: "approved_at" },
    { label: "Payment Status", key: "payment_status", sortable: false },
  ];

  return (
    <>
      <div className="flex flex-col gap-4">
        <PageTableHeader
          breadcrumbs={items}
          shouldShow
        />

        {/* Payout Provider Status - COMMENTED OUT */}
        {/* {payoutProviderStatus && (
          <div
            className={`border rounded-lg p-4 ${
              payoutProviderStatus.is_enabled
                ? "bg-green-50 border-green-200"
                : "bg-yellow-50 border-yellow-200"
            }`}
          >
            <div className="flex items-center gap-2">
              {payoutProviderStatus.is_enabled ? (
                <FiCheckCircle className="text-green-600 text-xl" />
              ) : (
                <FiAlertCircle className="text-yellow-600 text-xl" />
              )}
              <div>
                <p className="font-medium text-sm">
                  {payoutProviderStatus.is_enabled
                    ? "Instant Payout Enabled"
                    : "Instant Payout Not Configured"}
                </p>
                <p className="text-xs text-gray-600 mt-1">
                  {payoutProviderStatus.is_enabled
                    ? `Provider: ${payoutProviderStatus.provider_name} | Balance: ₹${payoutProviderStatus.available_balance}`
                    : "Configure payout provider to enable instant payouts"}
                </p>
              </div>
            </div>
          </div>
        )} */}

        {/* Bulk Actions */}
        {selectedItems.length > 0 && (
          <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 flex items-center justify-between">
            <span className="text-sm font-medium text-purple-900">
              {selectedItems.length} expense(s) selected
            </span>
            <div className="flex gap-2">
              <DefaultButton
                name="Clear Selection"
                variant="outline"
                size="sm"
                onClick={() => setSelectedItems([])}
              />
              <DefaultButton
                name={`Add ${selectedItems.length} to Payroll`}
                variant="primary"
                size="sm"
                onClick={handleBulkAddToPayroll}
                icon={<FiBriefcase />}
              />
            </div>
          </div>
        )}

        <div className="bg-white">
          <CommonTable
            TableHeaderData={TableHeaderData}
            tabledata={tableData.data || []}
            currentPage={currentPage}
            totalData={totalData}
            setCurrentPage={handlePageChange}
            loading={loading}
            action={tableData.action}
          />
        </div>
      </div>

      {/* Action Modal */}
      <CommonModal
        show={modal.show}
        headerTitle={modal.title}
        onClose={() =>
          setModal({ show: false, item: null, actionType: null, notes: "" })
        }
        modalSize="md"
      >
        <div className="space-y-4 py-4">
          <p className="text-gray-600 text-sm">{modal.message}</p>

          {/* Warning for instant payout */}
          {modal.actionType === "instant_payout" && (
            <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
              <p className="text-sm text-blue-900">
                💡 <strong>Note:</strong> Amount will be transferred to employee's bank account within 1-2 business days.
              </p>
            </div>
          )}

          {/* Warning for payroll */}
          {modal.actionType === "add_to_payroll" && (
            <div className="p-3 bg-purple-50 rounded-lg border border-purple-200">
              <p className="text-sm text-purple-900">
                💼 <strong>Note:</strong> This expense will be added to the employee's next payroll cycle.
              </p>
            </div>
          )}

          {/* Notes Input */}
          <DefaultTextarea
            label={
              modal.actionType === "mark_paid"
                ? "Payment Confirmation Details *"
                : "Notes (Optional)"
            }
            value={modal.notes}
            onChange={(e) =>
              setModal((prev) => ({ ...prev, notes: e.target.value }))
            }
            placeholder={
              modal.actionType === "mark_paid"
                ? "Transaction ID, payment date, etc."
                : "Add any comments..."
            }
            rows={3}
          />

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4">
            <DefaultButton
              name="Cancel"
              variant="outline"
              size="md"
              onClick={() =>
                setModal({ show: false, item: null, actionType: null, notes: "" })
              }
              disabled={modal.loading}
            />

            <DefaultButton
              name={
                modal.actionType === "instant_payout"
                  ? "Process Payout"
                  : modal.actionType === "add_to_payroll"
                  ? "Add to Payroll"
                  : "Mark as Paid"
              }
              variant="primary"
              size="md"
              loading={modal.loading}
              onClick={handleActionConfirm}
              icon={
                modal.actionType === "instant_payout" ? (
                  <FiDollarSign />
                ) : modal.actionType === "add_to_payroll" ? (
                  <FiBriefcase />
                ) : (
                  <FiCheckCircle />
                )
              }
            />
          </div>
        </div>
      </CommonModal>
    </>
  );
};

export default FinanceQueue;

